import { getLogoutEpoch } from './logout-teardown-signal';

// Requests the boot entry starts before React renders, handed to the query that would otherwise
// start them. A hand-off is one-shot and keyed by user (and office). A sign-out or a change of
// identity bumps the logout epoch, which voids everything started before it, and an entry nobody
// takes is dropped when its window ends, so a response never outlives the session that asked.

export const BOOT_READ_TTL_MS = 15_000;

interface BootRead {
  readonly response: Promise<unknown>;
  readonly startedAt: number;
  readonly logoutEpoch: number;
}

const bootReads = new Map<string, BootRead>();

export const bootReadKey = {
  organizations: (userId: string) => `organizations:${userId}`,
  platformAdmin: (userId: string) => `platform-admin:${userId}`,
  teamMembers: (userId: string, organizationId: string) =>
    `team-members:${userId}:${organizationId}`,
  featureGates: (userId: string, organizationId: string) =>
    `feature-gates:${userId}:${organizationId}`,
} as const;

function isCurrent(bootRead: BootRead): boolean {
  return (
    Date.now() - bootRead.startedAt <= BOOT_READ_TTL_MS && bootRead.logoutEpoch === getLogoutEpoch()
  );
}

/** Voids every read, started or answered: called when a sign-out is seen before the app mounts. */
export function clearBootReads(): void {
  bootReads.clear();
}

export function startBootRead(key: string, start: () => PromiseLike<unknown>): void {
  if (bootReads.has(key)) {
    return;
  }
  const response = Promise.resolve(start());
  // Marks a rejection nobody takes as handled; `takeBootResponse` still sees it.
  response.catch(() => null);
  const bootRead: BootRead = { response, startedAt: Date.now(), logoutEpoch: getLogoutEpoch() };
  bootReads.set(key, bootRead);
  setTimeout(() => {
    if (bootReads.get(key) === bootRead) {
      bootReads.delete(key);
    }
  }, BOOT_READ_TTL_MS);
}

/**
 * The boot read's response, once, or `undefined` when there is none, it is not the current
 * session's any more, it did not answer within its window, it failed, or it answered with an
 * error: the caller then makes its own request, so a bad boot read costs one repeat request and
 * changes nothing else.
 */
export async function takeBootResponse<Response extends { readonly error: unknown }>(
  key: string,
): Promise<Response | undefined> {
  const bootRead = bootReads.get(key);
  bootReads.delete(key);
  if (!bootRead || !isCurrent(bootRead)) {
    return undefined;
  }
  let deadline: number | undefined;
  try {
    const response = await Promise.race([
      bootRead.response as Promise<Response>,
      new Promise<undefined>((resolve) => {
        deadline = setTimeout(
          resolve,
          Math.max(0, BOOT_READ_TTL_MS - (Date.now() - bootRead.startedAt)),
        );
      }),
    ]);
    return response && !response.error && isCurrent(bootRead) ? response : undefined;
  } catch {
    return undefined;
  } finally {
    clearTimeout(deadline);
  }
}

/**
 * What a query awaits in place of its own request: the boot read's response when one was
 * started, otherwise `request()` itself, called in the same tick as before so a query with no
 * boot read behaves exactly as it did.
 */
export function bootOrRequest<Response extends { readonly error: unknown }>(
  key: string,
  request: () => PromiseLike<Response>,
): PromiseLike<Response> {
  if (!bootReads.has(key)) {
    return request();
  }
  return takeBootResponse<Response>(key).then(async (response) => response ?? (await request()));
}
